import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import type {
    FrageKritikalitaet,
    FrageStatus,
    FrageZielobjekt,
    HinweisArt,
    KatalogKapitel,
    Planungsfrage,
    Planungshinweis,
    Planungsphase,
    PlanungStatus,
    ProjektMerkmale,
    SchwellenwertErgebnis,
} from '../components/planungsphase/types';
import { normalisiereUnterlagen } from '../components/planungsphase/unterlagenLogic';
import {
    planungAmpel,
    vorankuendigungFrist,
    type PlanungAmpel,
} from '../components/planungsphase/planungAmpel';
import { weiterAusLage } from '../components/planungsphase/planungKachel';

/**
 * Sammelabfrage der Planungsampel ueber viele Projekte (Dashboard,
 * Aufgabenliste, Projektliste). NUR LESEN — Entscheid E9: kein Statuswechsel,
 * kein Stempel, die Feststellung bleibt beim Menschen.
 *
 * Die Regeln stehen in `planungAmpel.ts` (pure, getestet); hier steht nur,
 * was ohne Supabase nicht zu haben ist: `planungsphasen`, `planungsfragen`
 * und `planungshinweise` fuer alle uebergebenen Projekte, in Stapeln je
 * `.in('project_id', …)` und darin gepaged.
 *
 * ═══ STAPEL UND SEITEN ═════════════════════════════════════════════════════
 * `.in()` haengt jede Projektkennung an die URL — bei dreistelliger
 * Projektzahl reisst das die Laengengrenze (`unbookedReportsService.ts`).
 * Deshalb hoechstens `STAPEL` Projekte je Abfrage. Innerhalb eines Stapels
 * deckelt PostgREST OHNE MELDUNG bei 1000 Zeilen; 100 Projekte mit je 200
 * Fragen sind 20 000 Zeilen — ohne `.range()` saehe man ein Zwanzigstel und
 * laese daraus „nur wenige Fragen offen" (Muster `fetchPlanungsfragen`).
 *
 * ═══ FEHLER ════════════════════════════════════════════════════════════════
 * Ausfall darf nie wie Entwarnung aussehen. Scheitert eine Abfrage, bekommen
 * ALLE Projekte des betroffenen Stapels die Farbe `fehler`, und `lesefehler`
 * nennt den Grund; die uebrigen Stapel werden weiter geladen. Auch eine
 * fehlende Tabelle ist hier ein Fehler und keine leere Lage — der Lesepfad
 * des Moduls (`fetchPlanungsphase`) darf fail-open sein, weil dort der Nutzer
 * die leere Maske sieht; eine Ampel ohne Daten wuerde dagegen grau oder
 * gruen leuchten.
 *
 * RLS filtert: Projekte, die der Nutzer nicht sehen darf, kommen ohne Zeilen
 * zurueck und erscheinen als `grau` — das ist dieselbe Sichtbarkeit wie in
 * der Projektliste, kein Nachbau einer Zugriffsregel im Client.
 *
 * ═══ WEITER-TEXT (Leitfaden, Stufe L3) ═════════════════════════════════════
 * Aus derselben geladenen Lage rechnet `weiterAusLage` je Projekt die
 * naechste Handlung („Weiter: Antworten einarbeiten · …") — keine weitere
 * Abfrage. Lesefehler oder kaputte Zeile → `''`, nie geraten. Warum die
 * Fragespalten dafuer NICHT erweitert werden, steht bei `FRAGE_SPALTEN`.
 */

const T_PHASE = 'planungsphasen';
const T_FRAGE = 'planungsfragen';
const T_HINWEIS = 'planungshinweise';

/** Projekte je `.in()`-Abfrage — 100 UUIDs sind rund 3,7 kB URL. */
const STAPEL = 100;
/** PostgREST-Deckel je Antwort. */
const SEITE = 1000;

/** Nur, was die Ampel liest (`pruefeFreigabe`, `istUeberfaellig`,
 *  `faelligeStatuswechsel`, Kennzahlen). Fragetext, Antwort und Bewertung
 *  bleiben draussen — bei 20 000 Zeilen zaehlt jede Spalte.
 *
 *  Der Weiter-Text (`leitfadenStand`) liest darueber hinaus nur an EINER
 *  Stelle Ungelesenes: Station 4 zaehlt mit `nichtMehrZutreffend` (`antwort`,
 *  `bewertung`, `eigene_frage`) und `neueFragenNachziehen` (`sortierung`)
 *  ihren Vermerk „… gegenstandslos möglich / nachziehbar". Die Spalten
 *  bleiben trotzdem draussen (`antwort`/`bewertung` sind Freitext, bei
 *  20 000 Zeilen Megabytes), denn der Vermerk erreicht den Weiter-Text nie:
 *  Station 4 ist mit der ersten Frage `erledigt` und wird damit nie die
 *  naechste Station, und `weiterText` zeigt nur den Stand der naechsten.
 *  Ohne Fragen ist sie `offen` — dann gibt es nichts, wofuer die Spalten
 *  zaehlten. Die leeren Felder aus `mapFrage` treffen also hoechstens den
 *  Vermerk, nie die Station oder den Text (Test „fehlende Textspalten"). */
const FRAGE_SPALTEN = 'id, project_id, planung_id, code, kritikalitaet, status, frist_am, gestellt_am, zielobjekt';
/** `pruefeFreigabe` braucht Art, Versandstempel und Fragecodes. */
const HINWEIS_SPALTEN = 'id, project_id, planung_id, nummer, art, versendet_am, frage_codes';

export interface AmpelProjekt {
    projectId: string;
    /** Ausfuehrungsbeginn, Rueckfall Projektstart — bestimmt der Aufrufer. */
    baustartIso: string | null;
    lvHatPlanungsleistung?: boolean;
}

export interface PlanungsAmpeln {
    /** Je uebergebener Projektkennung genau eine Ampel. */
    ampeln: Record<string, PlanungAmpel>;
    /** Je uebergebener Projektkennung der Weiter-Text des Leitfadens
     *  (`weiterAusLage`); `''` ohne Phase, wenn nichts zu tun ist, bei
     *  Lesefehler des Stapels und bei kaputter Zeile. */
    weiter: Record<string, string>;
    /** Erster Fehlertext, wenn mindestens ein Stapel nicht lesbar war. */
    lesefehler: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// ZEILEN → TYPEN
// ═══════════════════════════════════════════════════════════════════════════

type Zeile = Record<string, unknown>;

const text = (v: unknown): string => (typeof v === 'string' ? v : '');
const textOderNull = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const zahl = (v: unknown, sonst: number): number => (typeof v === 'number' && isFinite(v) ? v : sonst);
const auswahl = <T extends string>(v: unknown, erlaubt: readonly T[], sonst: T): T =>
    typeof v === 'string' && (erlaubt as readonly string[]).includes(v) ? (v as T) : sonst;

const PHASE_STATUS: readonly PlanungStatus[] = ['entwurf', 'laufend', 'abgeschlossen'];
const FRAGE_STATUS: readonly FrageStatus[] = ['offen', 'gestellt', 'beantwortet', 'teilbeantwortet', 'nicht_beantwortet', 'gegenstandslos'];
const KRITIKALITAET: readonly FrageKritikalitaet[] = ['A', 'B', 'C'];
const ZIELOBJEKT: readonly FrageZielobjekt[] = ['sigeplan', 'vorankuendigung', 'unterlage', 'ausschreibung', 'keins'];
const HINWEIS_ART: readonly HinweisArt[] = ['hinweis', 'bedenken', 'gefahr'];

/** Vorgaben gehen im Zweifel in Richtung „offen", nie in Richtung
 *  Entwarnung: unbekannter Phasenstatus zaehlt als laufend, unbekannter
 *  Fragestatus als offen, unbekannte Hinweisart deckt keine Frage. */
const mapPhase = (row: Zeile): Planungsphase => ({
    id: text(row.id),
    project_id: text(row.project_id),
    status: auswahl(row.status, PHASE_STATUS, 'entwurf'),
    baubeschreibung: text(row.baubeschreibung),
    bauaufgabe: text(row.bauaufgabe),
    merkmale: (row.merkmale && typeof row.merkmale === 'object' && !Array.isArray(row.merkmale))
        ? (row.merkmale as ProjektMerkmale) : {},
    katalog_version: zahl(row.katalog_version, 1),
    lph: typeof row.lph === 'number' ? row.lph : null,
    pruefung: (row.pruefung && typeof row.pruefung === 'object')
        ? (row.pruefung as SchwellenwertErgebnis) : null,
    unterlagen: normalisiereUnterlagen(row.unterlagen),
    abgeschlossen_am: textOderNull(row.abgeschlossen_am),
    created_at: text(row.created_at),
    updated_at: text(row.updated_at),
});

/** Vollstaendige `Planungsfrage` aus den gelesenen Spalten; die nicht
 *  gelesenen Textfelder sind leer. */
const mapFrage = (row: Zeile): Planungsfrage => ({
    id: text(row.id),
    planung_id: text(row.planung_id),
    project_id: text(row.project_id),
    code: text(row.code),
    kapitel: (text(row.kapitel) || 'A') as KatalogKapitel,
    frage: '',
    adressat: [],
    empfaenger_name: null,
    empfaenger_email: null,
    typ: 'T',
    kritikalitaet: auswahl(row.kritikalitaet, KRITIKALITAET, 'B'),
    status: auswahl(row.status, FRAGE_STATUS, 'offen'),
    antwort: '',
    bewertung: '',
    rechtsgrundlage: [],
    zielobjekt: auswahl(row.zielobjekt, ZIELOBJEKT, 'keins'),
    gestellt_am: textOderNull(row.gestellt_am),
    frist_am: textOderNull(row.frist_am),
    beantwortet_am: null,
    uebernommen_am: null,
    sortierung: 0,
    eigene_frage: false,
    created_at: '',
    updated_at: '',
});

const mapHinweis = (row: Zeile): Planungshinweis => ({
    id: text(row.id),
    planung_id: text(row.planung_id),
    project_id: text(row.project_id),
    nummer: zahl(row.nummer, 0),
    art: auswahl(row.art, HINWEIS_ART, 'hinweis'),
    betreff: '',
    sachverhalt: '',
    gefaehrdung: '',
    folgen: '',
    rechtsgrundlage: [],
    forderung: '',
    frist_am: null,
    folge_bei_fristablauf: '',
    empfaenger_name: '',
    empfaenger_email: '',
    adressat_ist_bauherr: false,
    verteiler: [],
    frage_codes: Array.isArray(row.frage_codes)
        ? row.frage_codes.filter((c): c is string => typeof c === 'string') : [],
    versendet_am: textOderNull(row.versendet_am),
    reaktion: '',
    reaktion_am: null,
    erstellt_von_name: '',
    created_at: '',
    updated_at: '',
});

// ═══════════════════════════════════════════════════════════════════════════
// LESEN
// ═══════════════════════════════════════════════════════════════════════════

type Antwort = { data: unknown; error: unknown };

/**
 * Liest alle Seiten einer Abfrage. Wirft beim ersten Fehler — der Aufrufer
 * macht daraus `fehler` fuer den ganzen Stapel. Sortierung nach
 * `project_id, id`: eindeutig, sonst erscheinen Zeilen beim Blaettern
 * doppelt oder fallen aus.
 */
const ladeSeiten = async <T>(
    abfrage: (von: number, bis: number) => PromiseLike<Antwort>,
    map: (row: Zeile) => T,
): Promise<T[]> => {
    const alle: T[] = [];
    for (let von = 0; ; von += SEITE) {
        const { data, error } = await abfrage(von, von + SEITE - 1);
        if (error) throw new Error(describeError(error));
        const seite = Array.isArray(data) ? (data as Zeile[]) : [];
        alle.push(...seite.map(map));
        if (seite.length < SEITE) return alle;
    }
};

interface StapelLage {
    phasen: Planungsphase[];
    fragen: Planungsfrage[];
    hinweise: Planungshinweis[];
}

const ladeStapel = async (ids: string[]): Promise<StapelLage> => {
    const [phasen, fragen, hinweise] = await Promise.all([
        // `*` statt Spaltenliste: `unterlagen` kommt aus Migration Nr. 156,
        // und eine fehlende Spalte darf nicht die ganze Abfrage kippen —
        // `mapPhase` liefert dann eine leere Checkliste (wie `fetchPlanungsphase`).
        ladeSeiten(
            (von, bis) => supabase
                .from(T_PHASE)
                .select('*')
                .in('project_id', ids)
                .order('project_id', { ascending: true })
                .order('id', { ascending: true })
                .range(von, bis),
            mapPhase,
        ),
        ladeSeiten(
            (von, bis) => supabase
                .from(T_FRAGE)
                .select(FRAGE_SPALTEN)
                .in('project_id', ids)
                .order('project_id', { ascending: true })
                .order('id', { ascending: true })
                .range(von, bis),
            mapFrage,
        ),
        ladeSeiten(
            (von, bis) => supabase
                .from(T_HINWEIS)
                .select(HINWEIS_SPALTEN)
                .in('project_id', ids)
                .order('project_id', { ascending: true })
                .order('id', { ascending: true })
                .range(von, bis),
            mapHinweis,
        ),
    ]);
    return { phasen, fragen, hinweise };
};

const gruppiere = <T extends { project_id: string }>(zeilen: T[]): Map<string, T[]> => {
    const map = new Map<string, T[]>();
    zeilen.forEach(z => {
        const liste = map.get(z.project_id);
        if (liste) liste.push(z); else map.set(z.project_id, [z]);
    });
    return map;
};

/** Die Ampel, wenn nicht gelesen werden konnte — nie gruen, nie grau. */
const fehlerAmpel = (projekt: AmpelProjekt | undefined): PlanungAmpel => ({
    farbe: 'fehler',
    gruende: [],
    quote: { geklaert: 0, gesamt: 0 },
    ueberfaelligA: 0,
    abgelaufeneFristen: 0,
    vorankuendigungFrist: vorankuendigungFrist(projekt ? projekt.baustartIso : null),
});

/**
 * Ampeln fuer viele Projekte. `heuteIso` ist der Tag in Ortszeit
 * (`heuteLokalIso`), vom Aufrufer vorgegeben — kein `new Date()` hier, damit
 * Dashboard, Aufgabenliste und Test denselben Tag meinen.
 */
export const fetchPlanungsAmpeln = async (
    projekte: AmpelProjekt[],
    heuteIso: string,
): Promise<PlanungsAmpeln> => {
    const ampeln: Record<string, PlanungAmpel> = {};
    const weiter: Record<string, string> = {};
    const jeId = new Map<string, AmpelProjekt>();
    (projekte || []).forEach(p => {
        if (p && p.projectId && !jeId.has(p.projectId)) jeId.set(p.projectId, p);
    });
    const ids = Array.from(jeId.keys());
    let lesefehler: string | null = null;

    for (let i = 0; i < ids.length; i += STAPEL) {
        const stapel = ids.slice(i, i + STAPEL);
        let lage: StapelLage;
        try {
            lage = await ladeStapel(stapel);
        } catch (e) {
            if (!lesefehler) lesefehler = describeError(e);
            stapel.forEach(id => {
                ampeln[id] = fehlerAmpel(jeId.get(id));
                weiter[id] = '';
            });
            continue;
        }

        const phaseJeProjekt = new Map(lage.phasen.map(p => [p.project_id, p]));
        const fragenJeProjekt = gruppiere(lage.fragen);
        const hinweiseJeProjekt = gruppiere(lage.hinweise);
        stapel.forEach(id => {
            const projekt = jeId.get(id);
            const phase = phaseJeProjekt.get(id) || null;
            const fragen = fragenJeProjekt.get(id) || [];
            const hinweise = hinweiseJeProjekt.get(id) || [];
            const baustartIso = projekt ? projekt.baustartIso : null;
            try {
                ampeln[id] = planungAmpel({
                    phase,
                    fragen,
                    hinweise,
                    heuteIso,
                    baustartIso,
                    lvHatPlanungsleistung: projekt ? projekt.lvHatPlanungsleistung : undefined,
                });
            } catch (e) {
                // Eine kaputte Zeile darf nicht die ganze Liste reissen —
                // und auch nicht still gruen werden.
                if (!lesefehler) lesefehler = describeError(e);
                ampeln[id] = fehlerAmpel(projekt);
                weiter[id] = '';
                return;
            }
            // Faengt selbst ab: scheitert nur der Leitfaden, bleibt die Ampel.
            weiter[id] = weiterAusLage({ phase, fragen, hinweise, heuteIso, baustartIso });
        });
    }

    return { ampeln, weiter, lesefehler };
};
